import { FC, Dispatch, SetStateAction, useState } from 'react';
import {
  Typography,
  Stack,
  Skeleton,
  IconButton,
  Tooltip,
  Zoom,
  useMediaQuery,
  ButtonBase,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import {
  Download as DownloadIcon,
  ArrowBack,
  Menu as MenuIcon,
  NavigateBefore,
  NavigateNext,
  PersonOutline,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { shallowEqual } from 'react-redux';
import {
  useEditPageTask,
  useRoutedTaskId,
} from 'src/components/hooks/useEditPageTask';
import { useTaskOutputActions } from 'src/components/hooks/useTaskOutputActions';
import { useEditSaveStatus } from 'src/components/hooks/useEditSaveStatus';
import { setEditBarSlot } from './editBarSlot';
import { useSiblingEditNavigation } from 'src/components/hooks/useSiblingEditNavigation';
import { AssignTaskDialog } from 'src/components/molecules/AssignTaskDialog';
import { useWorkspacesControllerGetWorkspaceByIdQuery } from 'src/app/services/api.generated';
import { memberDisplayName } from 'src/utils/displayUsername';
import { isOpenableTaskState } from 'src/utils/taskProgressStreamUtils';
import { ZgwWriteBackButton } from 'src/components/organisms/zgw/ZgwWriteBackButton';

type EditPageHeaderPropsType = {
  setOpenSandwichMenu?: Dispatch<SetStateAction<boolean>>;
  /** Layout spacer only; skip interactive actions. */
  spacer?: boolean;
};

const EditPageHeaderActions: FC<{ spacer?: boolean }> = ({ spacer = false }) => {
  const { t } = useTranslation();
  const {
    id,
    isGenerating,
    isStillGenerating,
    isGenerateMutationLoading,
    canDownload,
    generateDocument,
    downloadDocument,
  } = useTaskOutputActions();
  const saveStatus = useEditSaveStatus();

  const taskState = useEditPageTask((task) => task?.state);
  const canEdit = isOpenableTaskState(taskState);
  const generateLabel = isStillGenerating
    ? t('documentStillGenerating')
    : t('generateDoc');

  return (
    <Stack
      sx={{ flexShrink: 0 }}
      data-testid="edit-page-header-actions"
      direction="row"
      spacing={1}
      alignItems="center"
    >
      {canEdit && saveStatus !== 'idle' && (
        <Tooltip
          title={saveStatus === 'saving' ? t('editPageSaveConfirmation') : ''}
        >
          <Typography
            role="status"
            variant="caption"
            color={saveStatus === 'error' ? 'error' : 'text.secondary'}
            sx={{ userSelect: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {saveStatus === 'saving'
              ? t('editPageSaving')
              : t('editPageSaveFailed')}
          </Typography>
        </Tooltip>
      )}
      <Tooltip title={!canEdit && id ? t('editPage.processing.title') : ''}>
        <span>
          <LoadingButton
            disabled={!id || spacer || !canEdit}
            loading={isGenerating || isGenerateMutationLoading}
            loadingPosition="start"
            onClick={generateDocument}
            size="small"
            variant="outlined"
            title={
              isGenerating
                ? (t(
                    isStillGenerating
                      ? 'documentStillGenerating'
                      : 'documentIsGenerating'
                  ) as string)
                : undefined
            }
            sx={{ color: 'text.primary', flexShrink: 0 }}
          >
            {generateLabel}
          </LoadingButton>
        </span>
      </Tooltip>
      {!spacer && <ZgwWriteBackButton taskId={id} hasExport={canDownload && canEdit} />}
      {canDownload && canEdit && (
        <Zoom in={canDownload} timeout={canDownload ? 500 : 100}>
          <IconButton
            onClick={downloadDocument}
            color="primary"
            size="small"
            aria-label={t('download') as string}
          >
            <DownloadIcon />
          </IconButton>
        </Zoom>
      )}
    </Stack>
  );
};

export const EditPageHeader: FC<EditPageHeaderPropsType> = ({
  setOpenSandwichMenu,
  spacer = false,
}) => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [assignOpen, setAssignOpen] = useState(false);
  const routeTaskId = useRoutedTaskId();
  // Title and actions come only from the routed document.
  const { id, name, numberOfPages, workspaceId, assigneeId } = useEditPageTask(
    (task) => ({
      id: task?.id,
      name: task?.name,
      numberOfPages: task?.numberOfPages,
      workspaceId: task?.workspace?.id,
      assigneeId: task?.assigneeId,
    }),
    shallowEqual
  );

  // Same query as AssignTaskDialog, so the cache is shared.
  const { data: workspace } = useWorkspacesControllerGetWorkspaceByIdQuery(
    { id: workspaceId || '' },
    { skip: !workspaceId || !assigneeId }
  );
  const assigneeLabel = assigneeId
    ? memberDisplayName(
        assigneeId,
        workspace?.memberEmails,
        workspace?.userDisplay
      ) || '—'
    : t('noAssignee');

  const { canGoPrev, canGoNext, goPrevTask, goNextTask, prevTask, nextTask } =
    useSiblingEditNavigation();

  const goToWorkspace = () => {
    navigate(workspaceId ? `/workspaces/${workspaceId}` : '/workspaces');
  };

  return (
    <Stack sx={{ backgroundColor: 'customGrey.light' }}>
      <Stack
        data-testid="edit-page-header-chrome"
        direction="row"
        sx={{
          px: { xs: 1.25, md: 2 },
          py: { xs: 1.25, md: 0 },
          height: { xs: 'auto', md: 56 },
          minHeight: 48,
          position: 'relative',
          columnGap: 1,
        }}
        justifyContent="center"
        alignItems="center"
      >
        {!isMd && setOpenSandwichMenu && (
          <IconButton
            onClick={() => setOpenSandwichMenu((prev) => !prev)}
            sx={{
              position: 'absolute',
              left: 8,
              color: 'primary.main',
            }}
            aria-label={t('editPage.menu') as string}
          >
            <MenuIcon fontSize="large" />
          </IconButton>
        )}
        {isMd ? (
          <>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flex: '1 1 0', minWidth: 180 }}
            >
              <Tooltip title={t('backToWorkspace')}>
                <IconButton
                  onClick={goToWorkspace}
                  size="small"
                  aria-label={t('backToWorkspace') as string}
                  sx={{ color: 'text.primary', flexShrink: 0 }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  prevTask
                    ? `${t('previousDocument')}: ${prevTask.name}`
                    : t('previousDocument')
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!canGoPrev}
                    onClick={goPrevTask}
                    aria-label={t('previousDocument') as string}
                  >
                    <NavigateBefore />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  nextTask
                    ? `${t('nextDocument')}: ${nextTask.name}`
                    : t('nextDocument')
                }
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={!canGoNext}
                    onClick={goNextTask}
                    aria-label={t('nextDocument') as string}
                  >
                    <NavigateNext />
                  </IconButton>
                </span>
              </Tooltip>
              <Stack sx={{ minWidth: 0, flex: '1 1 auto' }}>
                <Tooltip title={name || ''}>
                  <Typography
                    fontWeight="bold"
                    color="text.primary"
                    noWrap
                    sx={{ lineHeight: 1.25 }}
                  >
                    {!name ? <Skeleton width={80} /> : name}
                  </Typography>
                </Tooltip>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{ minWidth: 0 }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ flexShrink: 0, lineHeight: 1.25 }}
                  >
                    {!id ? (
                      <Skeleton width={70} />
                    ) : numberOfPages != null && numberOfPages > 0 ? (
                      t('pageCount', { count: numberOfPages })
                    ) : null}
                  </Typography>
                  {id && (
                    <Tooltip title={t('assignee')}>
                      <ButtonBase
                        onClick={() => setAssignOpen(true)}
                        sx={{
                          minWidth: 0,
                          gap: 0.25,
                          borderRadius: 1,
                          color: 'text.secondary',
                          '&:hover': { color: 'text.primary' },
                        }}
                      >
                        <PersonOutline sx={{ fontSize: 14 }} />
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ lineHeight: 1.25 }}
                        >
                          {assigneeLabel}
                        </Typography>
                      </ButtonBase>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </Stack>
            {/* The editor toolbar portals its tools in here. */}
            <Stack
              ref={spacer ? undefined : setEditBarSlot}
              data-testid="edit-page-header-tools"
              direction="row"
              alignItems="center"
              sx={{ flex: '0 0 auto', px: 1 }}
            />
            <Stack
              direction="row"
              justifyContent="flex-end"
              sx={{ flex: '1 1 0', minWidth: 0 }}
            >
              {/* Keyed so no output/download state carries over between documents. */}
              <EditPageHeaderActions key={routeTaskId} spacer={spacer} />
            </Stack>
          </>
        ) : (
          <>
            <Tooltip title={t('backToWorkspace')}>
              <IconButton
                onClick={goToWorkspace}
                sx={{
                  position: 'absolute',
                  left: 48,
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
                aria-label={t('backToWorkspace') as string}
              >
                <ArrowBack />
              </IconButton>
            </Tooltip>
            <Stack
              alignItems="center"
              sx={{ maxWidth: 'calc(100vw - 120px)', minWidth: 0, px: 1 }}
            >
              <Typography
                align="center"
                fontWeight="bold"
                color="text.primary"
                noWrap
                textOverflow="ellipsis"
                overflow="hidden"
                sx={{ width: '100%', fontSize: '0.95rem' }}
              >
                {!name ? <Skeleton width={80} /> : name}
              </Typography>
              {numberOfPages != null && (
                <Typography
                  variant="caption"
                  align="center"
                  color="text.primary"
                  sx={{ opacity: 0.75, lineHeight: 1.2 }}
                >
                  {t('pageCount', { count: numberOfPages })}
                </Typography>
              )}
            </Stack>
          </>
        )}
      </Stack>
      {id && (
        <AssignTaskDialog
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          taskId={id}
          workspaceId={workspaceId}
          currentAssigneeId={assigneeId}
        />
      )}
    </Stack>
  );
};
